import { useMemo, useState } from 'react'

import { ArrowDownTrayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Alert, Modal, Select } from 'flowbite-react'
import { useParams } from 'react-router-dom'

import Loader from '@/components/Loader'
import TableItem from '@/components/Table'
import PaginationItem from '@/components/Table/pagination-item'
import { useDetalhePedidoContext } from '@/contexts/detalhe-pedido.context'
import { IPedidosModalDetailProps } from '@/interfaces/pedidos'
import { TABLE_HEADERS_DETALHES_PEDIDO_ITENS } from '@/pages/Pedidos/constants'
import { useGetBuscarItensVenda } from '@/hooks/queries/use-get-buscar-itens-venda'
import { getBuscarComprovante } from '@/services/pedidos'
import useLocalStorage from '@/hooks/common/use-local-storage'
import toastService from '@/components/toast/toast-service'
import ActionList from '@/components/action-list'

const PedidosModalDetail = ({
  onClose,
  isOpen,
  agrupado
}: IPedidosModalDetailProps) => {
  const { pedido, operadoraId } = useDetalhePedidoContext()
  const [pageSize, setPageSize] = useState<number>(10)
  const [page, setPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [, setComprovante] = useLocalStorage(
    'comprovante',
    ''
  )
  const inicio = (page - 1) * pageSize

  const unidadePedidoId = pedido?.conteudo?.idUnidade ?? ''
  const { id } = useParams()

  const tipoVenda = operadoraId === 'vr-credito' || operadoraId === null ? 'VrCredito' : ''
  const { data: buscarItensVenda, isLoading: isLoadingBuscarItensVenda } = useGetBuscarItensVenda({
    params: {
      idPedidoVenda: id ?? '',
      idOperadora: operadoraId === 'vr-credito' ? '' : operadoraId ?? '',
      idUnidade: unidadePedidoId ?? '',
      inicio: inicio,
      comprimento: pageSize,
      procurar: '',
      campo: '',
      direcao: '',
      tipoVenda: tipoVenda,
      agrupar: true,
      situacoesItem: [
        'Ativo',
        'Devolvido',
        'AguardandoDevolver',
        'Finalizado',
        'CnabGerado'
      ]
    }
  })

  const quantidadeDeItems = buscarItensVenda?.conteudo?.quantidadeDeItems ?? 0

  const getComprovante = async (data: Record<string, any>) => {
    try {
      const result = await getBuscarComprovante(data.id)
      if (!result.sucesso) {
        throw new Error('Comprovante não encontrado')
      }

      setComprovante({ ...result.conteudo, ...data })
      window.open('/comprovante', '_blank')
      
    } catch (error) {
      toastService.alert('O comprovante não está disponível.')
    }
  }

  const customActions = [{ title: 'Baixar', action: getComprovante, icon: <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> }]

  const itens = useMemo(() => {
    const itensVenda = buscarItensVenda?.conteudo?.resultados ?? []
    const totalItensPages = Math.ceil((quantidadeDeItems ?? 0) / pageSize)

    setTotalPages(totalItensPages)

    return itensVenda
  }, [buscarItensVenda])

  const mappedItens = useMemo(() => {
    return (
      itens?.map((item) => ({
        cnpj: item.cnpjUnidade,
        funcionario: (
          <>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Nome:</span> {item?.nome ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">CPF:</span> {item?.cpf ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Matrícula:</span> {item?.matricula ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Cargo:</span> {item?.cargo ?? '-'}
            </span>
          </>
        ),
        linha: (
          <>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Código:</span> {item.codigoTarifa ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Linha:</span> {item.descricaoTarifa ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Valor Unitário:</span>{' '}
              {item.tarifa?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              }) ?? '-'}
            </span>
          </>
        ),
        recarga: (
          <>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Dias para compra:</span> {item.diasUso ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Quantidade por dia:</span> {item.quantidadePorDia ?? '-'}
            </span>
          </>
        ),
        economia: (
          <>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Economia gerada:</span>{' '}
              {(item.total - item.totalComEconomia)?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              }) ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-semibold">Valor Item:</span>{' '}
              {item.total?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              }) ?? '-'}
            </span>
            <span className="flex gap-3 justify-between">
              <span className="font-bold text-black">Total com Economia:</span>{' '}
              {item.totalComEconomia?.toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              }) ?? '-'}
            </span>
          </>
        ),
        actions: (
          <>
            {tipoVenda && (
              <ActionList actions={customActions} data={{ id: item?.id, nome: item?.nome ?? '-', cpf: item?.cpf ?? '-' }} />
            )}
          </>
        )
      })) ?? []
    );
  }, [itens]);

  return (
    <Modal
      popup
      show={isOpen}
      onClose={onClose}
      className='flex items-center'
      data-testid='modal-item-pedido'
      size='[100%]'
    >
      <Modal.Header>
        <p className='text-center font-bold p-3'>Detalhes do item do pedido</p>
      </Modal.Header>
      <Modal.Body>
        {agrupado ? (
          <div className='mb-3' data-testid='alerta-agrupamento'>
            <Alert color='warning' className='mt-3'>
              <div className='flex justify-center items-center'>
                <ExclamationTriangleIcon className='min-w-5 w-5 h-5 mr-2' />
                <span>
                  <p className='text-sm'>
                    Atenção! Este pedido possui itens que ultrapassaram o valor
                    máximo de compra permitido pela operadora.
                  </p>
                  <p className='text-sm'>
                    Os itens foram divididos respeitando o valor máximo, e os
                    critérios estabelecidos por esta operadora, para dar
                    sequência na compra do pedido!
                  </p>
                </span>
              </div>
            </Alert>
          </div>
        ) : (
          <></>
        )}
        <div>
          <div data-testid='table-itens'>
            {isLoadingBuscarItensVenda ? (
              <div
                className='h-96 w-full flex justify-center items-center'
                data-testid='loader-taxas'
              >
                <Loader size={'xl'} />
              </div>
            ) : (
              <TableItem
                isActions={false}
                isRowClicable={false}
                data={mappedItens || []}
                headers={TABLE_HEADERS_DETALHES_PEDIDO_ITENS}
                deleteAction={() => void 0}
                idKey='vendaId'
                isIdle={isLoadingBuscarItensVenda}
              />
            )}
            <PaginationItem
              totalItems={quantidadeDeItems}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={(pageParam: number) => setPage(pageParam)}
              forceDisplay={true}
            >
              <Select
                id='select-page-size'
                data-testid='select-page-size'
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
                defaultValue={pageSize}
              >
                <option value='10'>10</option>
                <option value='25'>25</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Select>
            </PaginationItem>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default PedidosModalDetail
